import { useEffect } from 'react'
import type { RefObject } from 'react'

/**
 * Run `callback` whenever a `mousedown` event lands outside the given element.
 *
 * `ref.current` is checked at event time — passing a freshly-created or
 * initially `null` ref is safe. Listens on `document` and unsubscribes on
 * unmount or when the ref changes.
 *
 * Use for dropdowns, popovers and any "click outside to dismiss" UX.
 */
export const useClickOutside = <T extends HTMLElement = HTMLElement>(
    ref: RefObject<T | null>,
    callback: (event: MouseEvent) => void,
): void => {
    useEffect(() => {
        const handler = (event: MouseEvent) => {
            const el = ref.current
            if (!el) return
            if (event.target instanceof Node && !el.contains(event.target)) {
                callback(event)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [ref, callback])
}
